import { App, AppSubscriptionCountry } from '../models/app';
import { getAppMarketMode } from '../utils/session';
import {
  availableSubscriptionsLength,
  isOnlyAndroidApp,
  isHipaaComplaintApp, isWebApp, isWirelessManagerApp, isYelpApp,
} from './app-details';
import { AppMode } from '../models/enums';
import { AppActions } from '../components/AppDetails/AppActions/types';
import { getMerchantIdFromStorage } from '../utils/sessionStorage';
import { ApiQuery, url } from '../utils/api';
import { CLOVER_DEV_ID, ENGINE_NAMES } from '../utils/constants';

const getActionForUninstalledApp = (app: App): AppActions => {
  if (isYelpApp(app)) return AppActions.YelpConnect;
  else return AppActions.PreInstall;
}

export const getMainActionsForApp = (appDetails: App): AppActions[] => {
  const appMode = getAppMarketMode();
  if (appMode === AppMode.Public) return [AppActions.PreInstall];
  let result = [];
  const { installed } = appDetails;
  if (installed) {
    result.push(AppActions.Open);
  } else {
    //not installed
    result.push(getActionForUninstalledApp(appDetails));
  }
  return result;
};

export const getActionsForPricingModule = (app: App, availableSubscriptionsArr: AppSubscriptionCountry[]): Maybe<AppActions> => {
  let action: Maybe<AppActions>;
  if (app.installed && !!availableSubscriptionsArr && availableSubscriptionsArr.length > 1) {
    action = AppActions.PreChangePricing;
  }else if (!app.installed) {
    action = getActionForUninstalledApp(app);
  }
  return action;
}

export const canUninstallApp = (app: Maybe<App>, hasPermissionToInstallUninstall: boolean): boolean => {
  return !!app?.installed && !app.systemApp && hasPermissionToInstallUninstall;
};

export const isInstallAppButtonDisabled = (app: App, hasInstallUninstallPermission: boolean, isMerchantDelinquent: boolean, requiresAnotherPlan: boolean, availableSubscriptionsLen: number = availableSubscriptionsLength(app)): boolean => {
  const isHipaaComplaint = isHipaaComplaintApp(app);
  const { siteUrl, packageName } = app;
  return !availableSubscriptionsLen || !hasInstallUninstallPermission || isMerchantDelinquent || !isHipaaComplaint || !(siteUrl || packageName) || requiresAnotherPlan;
};

export const isOpenAppDisabled = (app: App, isMerchantDelinquent: boolean): boolean => isOnlyAndroidApp(app) || !isWebApp(app) || isMerchantDelinquent || !isHipaaComplaintApp(app);

export const canShowChangeSubscription = (app: App, hasInstallUninstallPermission: boolean, availableSubscriptionsLen: number = availableSubscriptionsLength(app)): boolean => {
  const { installed } = app;
  return !!installed && hasInstallUninstallPermission && availableSubscriptionsLen > 1 && isHipaaComplaintApp(app) && !isWirelessManagerApp(app);
};

const isCloverDev = (devId: Uuid): boolean => devId === CLOVER_DEV_ID;

const getEngineName = (name: string): string => {
  name = name.toLowerCase();

  if (name === 'bar tab auths') return 'tabs';

  return name.replace(' ', '-');
};

export const isCloverWebApp = (app: App): boolean => {
  const { developer } = app;
  const appName = getEngineName(app.name);

  return isCloverDev(developer.id) && ENGINE_NAMES.includes(appName);
};

// the launchUrl is the redirect URL on the server that adds the merchant_id, and oauth info before going to the developer's web app
// for clover apps, use siteUrl to avoid creating unnecessary authtokens
export const getAppLaunchUrl = (app: App): Maybe<string> => {
  const { siteUrl, id } = app;

  if (!siteUrl) return;
  if (isCloverWebApp(app)) return siteUrl;

  let merchantId = getMerchantIdFromStorage();

  if(isLaunchUrlPathAvailable(app)) {
    return getLaunchUrlPathOrNull(app);
  }
  const urlParams: Partial<ApiQuery> = {
    namespace: 'oauth',
    customQueries: [
      {
        client_id: id,
      },
    ],
  };

  if (merchantId) {
    urlParams.baseResource = 'merchants';
    urlParams.baseResourceId = merchantId;
  } else {
    urlParams.baseResource = 'authorize';
  }

  return url(urlParams);
};

export const isLaunchUrlPathAvailable = (app: App) : boolean => {
  return !!app.launchUrlPath;
}

export const getLaunchUrlPathOrNull = (app: App): string => {
  const { siteUrl, id, launchUrlPath } = app;
  let merchantId = getMerchantIdFromStorage();
  if(!!siteUrl && !!launchUrlPath) {
    const { origin } = new URL(siteUrl);
    const slash = launchUrlPath.charAt(0) === '/' ? '' : '/';
    return `${origin}${slash}${launchUrlPath}?merchant_id=${merchantId}&client_id=${id}`;
  } else {
    return ""
  }
}
